/**
 * Mixin for tracking PlanCard visibility using Intersection Observer API
 * Tracks individual PlanCard elements and updates store's quotesInSight state
 */
export default {
    data () {
        return {
            isVisible: false,
            hasBeenVisible: false,
            visibilityRatio: 0,
            visiblePlanIds: new Set(),
            planCardObservers: new Map()
        }
    },

    props: {
        visibilityThreshold: {
            type: [Number, Array],
            default: 0.1
        },
        visibilityRootMargin: {
            type: String,
            default: '0px'
        }
    },

    mounted () {
        this.setupVisibilityObserver()
        // Set up a mutation observer to watch for new PlanCard elements
        this.setupMutationObserver()
    },

    beforeDestroy () {
        this.destroyVisibilityObserver()
        this.destroyMutationObserver()
    },

    methods: {
        setupVisibilityObserver () {
            if ('IntersectionObserver' in window) {
                // Observer for component-level visibility
                this.observer = new IntersectionObserver(this.onVisibilityChange, {
                    threshold: this.visibilityThreshold,
                    rootMargin: this.visibilityRootMargin
                })

                // Observer for individual PlanCard elements
                this.planObserver = new IntersectionObserver(this.onPlanCardVisibilityChange, {
                    threshold: this.visibilityThreshold,
                    rootMargin: this.visibilityRootMargin
                })

                this.observer.observe(this.$el)
                this.observePlanCards()
            }
        },

        setupMutationObserver () {
            if ('MutationObserver' in window) {
                this.mutationObserver = new MutationObserver(() => {
                    // Re-observe PlanCards when DOM changes
                    this.observePlanCards()
                })

                this.mutationObserver.observe(this.$el, {
                    childList: true,
                    subtree: true
                })
            }
        },

        observePlanCards () {
            if (!this.planObserver) return

            // Find all PlanCard elements with data-plan-id attribute
            const planCards = this.$el.querySelectorAll('[data-plan-id]')

            planCards.forEach((card) => {
                const planId = card.getAttribute('data-plan-id')
                if (planId && !this.planCardObservers.has(planId)) {
                    this.planObserver.observe(card)
                    this.planCardObservers.set(planId, card)
                }
            })
        },

        onPlanCardVisibilityChange (entries) {
            let visibilityChanged = false

            entries.forEach((entry) => {
                const planId = entry.target.getAttribute('data-plan-id')
                if (!planId) return

                const wasVisible = this.visiblePlanIds.has(planId)
                const isVisible = entry.isIntersecting

                if (isVisible && !wasVisible) {
                    this.visiblePlanIds.add(planId)
                    visibilityChanged = true
                } else if (!isVisible && wasVisible) {
                    this.visiblePlanIds.delete(planId)
                    visibilityChanged = true
                }
            })

            if (visibilityChanged) {
                this.updateQuotesInSight()
            }
        },

        updateQuotesInSight () {
            // Update store with currently visible plan IDs
            const visibleIds = Array.from(this.visiblePlanIds)
            this.$store.commit('quotesInSight', visibleIds)

            // Emit event for any component that might want to listen
            this.$emit('plans-visibility-change', {
                visiblePlanIds: visibleIds,
                visibleCount: visibleIds.length
            })
        },

        destroyVisibilityObserver () {
            if (this.observer) {
                this.observer.disconnect()
                this.observer = null
            }
            if (this.planObserver) {
                this.planObserver.disconnect()
                this.planObserver = null
            }
            this.planCardObservers.clear()
        },

        destroyMutationObserver () {
            if (this.mutationObserver) {
                this.mutationObserver.disconnect()
                this.mutationObserver = null
            }
        },

        onVisibilityChange (entries) {
            const entry = entries[0]
            this.isVisible = entry.isIntersecting
            this.visibilityRatio = entry.intersectionRatio

            if (entry.isIntersecting && !this.hasBeenVisible) {
                this.hasBeenVisible = true
            }

            // Emit visibility change event for parent components
            this.$emit('visibility-change', {
                isVisible: this.isVisible,
                hasBeenVisible: this.hasBeenVisible,
                visibilityRatio: this.visibilityRatio,
                entry
            })
        },

        checkVisibility () {
            return {
                isVisible: this.isVisible,
                hasBeenVisible: this.hasBeenVisible,
                visibilityRatio: this.visibilityRatio,
                visiblePlanIds: Array.from(this.visiblePlanIds),
                visiblePlanCount: this.visiblePlanIds.size
            }
        },

        getVisiblePlanIds () {
            return Array.from(this.visiblePlanIds)
        },

        isPlanVisible (planId) {
            return this.visiblePlanIds.has(planId)
        }
    }
}
